import React, { type CSSProperties, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { useMediaQuery } from "usehooks-ts";
import { colors } from "../../../../../styles/color-tokens";

import { FileSelector } from "../../file-selector/file-selector";
import {
  cancelPickerUpload,
  dismissUploadPanel,
  filtersChanged,
  retryPickerUpload,
  userFilesSelected,
  filtersReset,
  pageSizeChanged,
  setPage,
  useMediaPickerGate,
  useMediaPickerModel,
} from "../model";
import { useUploadPanelLifecycle } from "../../file-selector/use-upload-panel-lifecycle";
import { selectCollection } from "../../../../../actions/mediaLibrary";

import { CollectionSelector } from "../../collection-selector/collection-selector";

import {
  MediaFilesFilters,
  useMediaFilesFilters,
} from "../../../../../entities/media-files";
import { useMediaCollections } from "../../../../../entities/media-collections";
import { MLSortBy } from "../../ml-sort-by";
import { MLToggleView } from "../../ml-toggle-view";
import { LibraryPayWall } from "../../library-pay-wall";
import { isFullLibrary } from "../../..";
import { useUGCUpsellModal } from "../../../../plans-modal";
import { Alert } from "../../../../../ui";
import { AlertType } from "../../../../../ui/alert";
import { CompactPagination } from "../compact-pagination";
import { Button, FormSize } from "../../../../../ui/ecosystems/forms";
import { CollectionFilesList } from "../../collection-files-list";
import { CollectionFilesTable } from "../../files-table";
import { useCurrentUser } from "../../../../../entities/current-user";
import { LoadingCover } from "../../../../../ui/cover";
import { PickerProps } from "../media-picker";
import { MediaFilesSelectedFilters } from "../../../../../entities/media-files/componets/filters/media-files-filters";
import { useFlexRowFreeWidth } from "../../../../../components/ui/use-flex-row-free-width";

export function LibraryPicker(props: PickerProps) {
  const dispatch = useDispatch();
  const user = useCurrentUser();
  const { isLoading: pendingCollections } = useMediaCollections();
  useMediaPickerGate();

  const { showUGCUpsellModal } = useUGCUpsellModal();
  const [viewType, setViewType] = useState<"list" | "grid">("grid");
  // Side by side only from lg: at md the dialog leaves too little room beside Filters for a name.
  const isRowLayout = useMediaQuery("(min-width: 64rem)", {
    defaultValue: false,
  });
  const pickerRef = useRef<HTMLDivElement>(null);
  const { rowRef, freeWidth } = useFlexRowFreeWidth({
    max: 400,
    selfRef: pickerRef,
  });

  const {
    uploadingStatus,
    uploadingProgressValue,
    uploads,
    uploadSummary,
    currentPage,
    pageSize,
    totalItems,
    currentCollection,
    collectionsTags,
    collections,
    media,
    filters,
  } = useMediaPickerModel();

  useUploadPanelLifecycle(uploadSummary, uploads);

  const handlePaginationChange = (page: number, newPageSize: number) => {
    if (newPageSize === pageSize) {
      dispatch(setPage(page));
    } else {
      dispatch(pageSizeChanged(newPageSize));
    }
  };

  const [filtersProps] = useMediaFilesFilters({
    values: filters,
    onChange: (nextFilters) => dispatch(filtersChanged(nextFilters)),
    tags: collectionsTags,
    onClear: () => dispatch(filtersReset()),
  });

  // Collections can already be loaded in Redux (e.g. fetched elsewhere in
  // the app) without any collection ever having been selected, which
  // otherwise leaves currentCollection permanently null and the whole tab
  // stuck behind the LoadingCover overlay - blocking every click, including
  // the collection dropdown itself. Fall back to selecting the first
  // available collection whenever nothing is currently selected.
  const firstCollectionId = collections[0]?._id;
  const currentCollectionId = currentCollection?._id;
  useEffect(() => {
    if (!pendingCollections && !currentCollectionId && firstCollectionId) {
      dispatch(selectCollection(firstCollectionId, true));
    }
  }, [pendingCollections, currentCollectionId, firstCollectionId, dispatch]);

  return (
    <LoadingCover isLoading={pendingCollections || !currentCollection}>
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-4">
        {/* Left section */}
        <div
          ref={rowRef}
          className="flex w-full min-w-0 items-center gap-2 lg:w-auto lg:flex-1"
        >
          <div
            ref={pickerRef}
            className="min-w-0 flex-1 lg:min-w-(--picker-floor) lg:flex-initial"
            style={
              {
                "--picker-floor": `${Math.min(240, freeWidth ?? 240)}px`,
              } as CSSProperties
            }
          >
            <LibraryPayWall>
              {({ isDisabled }) => (
                <CollectionSelector
                  selectedCollection={currentCollection ?? null}
                  collections={collections}
                  onChange={(collectionId) =>
                    dispatch(selectCollection(collectionId, true))
                  }
                  disabled={isDisabled}
                  triggerMaxWidth={isRowLayout ? (freeWidth ?? 240) : undefined}
                />
              )}
            </LibraryPayWall>
          </div>
          <div className="shrink-0">
            <MediaFilesFilters
              {...filtersProps}
              availableFilters={[
                "name",
                "description",
                "sourceName",
                "tags",
                "favorite",
                "mediaType",
                "usage",
              ]}
            />
          </div>
        </div>

        {/* Right section */}
        <div className="flex shrink-0 items-center gap-2">
          <LibraryPayWall>
            {({ isDisabled }) => (
              <MLSortBy
                disabled={isDisabled}
                value={filters.sort}
                onChange={(sort) =>
                  dispatch(
                    filtersChanged({
                      ...filters,
                      sort,
                    })
                  )
                }
              />
            )}
          </LibraryPayWall>
          <MLToggleView value={viewType} onChange={setViewType} />
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <MediaFilesSelectedFilters {...filtersProps} />
      </div>
      <FileSelectorContainer>
        <FileSelector
          uploadingStatus={uploadingStatus}
          uploadingProgressValue={uploadingProgressValue}
          uploads={uploads}
          uploadSummary={uploadSummary}
          onCancelUpload={(id) => dispatch(cancelPickerUpload(id) as never)}
          onRetryUpload={(id) =>
            dispatch(
              retryPickerUpload(id, {
                onFileUploaded: props.handleFileAdd,
              }) as never
            )
          }
          onDoneUploads={() => dispatch(dismissUploadPanel() as never)}
          limit={props.limit}
          isDisabled={props.limit <= props.selectedMediaFiles.size}
          onSelect={(files) =>
            dispatch(
              userFilesSelected(files, {
                onFileUploaded: props.handleFileAdd,
              }) as never
            )
          }
          title={`Upload into ${currentCollection?.name}`}
          renderOverlay={(props) => (
            <Overlay {...props}>
              <DropZoneTitle>Drop files to add to your post</DropZoneTitle>
            </Overlay>
          )}
        />
      </FileSelectorContainer>
      <Gallery>
        <Files>
          {React.createElement(views[viewType], {
            files: media,
            selectedMediaSet: props.selectedMediaFiles,
            selectedCollection: props.selectedCollection,
            onSelect: props.handleFileAdd,
            onRemove: props.handleFileRemove,
          })}
          {isFullLibrary(user) ? (
            <CompactPagination
              numItems={totalItems}
              currentPage={currentPage}
              pageSize={pageSize}
              pageSizeOptions={[12, 24, 48, 96]}
              onChange={handlePaginationChange}
            />
          ) : (
            <div style={{ marginTop: 12 }}>
              <Alert type={AlertType.info}>
                Upgrade your plan to view, sort and create posts from all
                assets. Upgraded plans can quickly source and save user
                generated content from hashtag searches, other users and more.
              </Alert>
              <div
                style={{
                  marginTop: 4,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  size={FormSize.large}
                  primary
                  type="button"
                  onClick={() => showUGCUpsellModal()}
                >
                  Upgrade your plan
                </Button>
              </div>
            </div>
          )}
        </Files>
      </Gallery>
    </LoadingCover>
  );
}

const views = {
  grid: CollectionFilesList,
  list: CollectionFilesTable,
};

const Files = styled.div`
  /* Pagination styles */
  margin-top: 20px;
`;

const Gallery = styled.div``;

const FileSelectorContainer = styled.div``;

const DropZoneTitle = styled.div`
  font-weight: 500;
  font-size: 24px;
  line-height: 29px;
  color: ${colors.black};
`;

const Overlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${colors.neutral400};
  box-sizing: border-box;
  border-radius: 5px;

  &:before {
    content: "";
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: ${colors.white}E6;
    backdrop-filter: blur(10px);
    z-index: -1;
  }
`;
